import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useFeesBySolutionContext } from 'sonata-platform-components';
import { LoadingSkeleton, Selector, type SelectorOptionPropsInterface } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { BillingSubsidiaryLoadingStyled } from './BillingSubsidiary.style';

import type { BillingSubsidiary as BillingSubsidiaryType } from 'sonata-static-storage/lib/schemas/billingSubsidiary/billingSubsidiary.schema';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';

function getUserBillingSubsidiary(
  user: UserInterface,
  billingSubsidiaries: BillingSubsidiaryType[]
): string | undefined {
  if (user.accountInformation?.billingSubsidiary) {
    return user.accountInformation?.billingSubsidiary;
  }
  if (user.billing?.country) {
    return billingSubsidiaries?.find((bs: BillingSubsidiaryType) => {
      return bs.countryCode === user.billing.country;
    })?.unit;
  }

  return undefined;
}

function getBillingSubsidiaryOptions(
  campaignBillingSubsidiary: string | undefined,
  billingSubsidiaries: BillingSubsidiaryType[]
) {
  // all subsidiaries that are billable and include campaign selected subsidiary in case it's not billable
  const subsidiaries = billingSubsidiaries
    .filter(bs => bs.billable)
    .concat(billingSubsidiaries.filter(bs => !bs.billable && bs._id === campaignBillingSubsidiary) || []);

  subsidiaries.sort(({ unit: unit1 }, { unit: uni2 }) => unit1.localeCompare(uni2));
  return subsidiaries.map(bs => ({
    id: bs.unit,
    label: bs.unit
  }));
}

function getDefaultBillingSubsidiaryValue(
  billingSubsidiary: string | undefined,
  billingSubsidiaries: BillingSubsidiaryType[],
  user: UserInterface
):
  | [
      {
        id: string;
        label: string;
      }
    ]
  | undefined {
  const userSubsidiary = user.accountInformation?.billingSubsidiary;
  const billingSubsidiaryOptions = getBillingSubsidiaryOptions(billingSubsidiary, billingSubsidiaries);
  const campaignOption = billingSubsidiaryOptions.find(o => o.id === billingSubsidiary);
  if (campaignOption) {
    return [campaignOption];
  }
  if (!userSubsidiary) {
    return;
  }
  const userOption = billingSubsidiaryOptions.find(o => o.id === userSubsidiary);
  const selectedSubsidiary = billingSubsidiaries.find(bs => bs.unit === userOption?.id);
  if (userOption && selectedSubsidiary?.billable) {
    return [userOption];
  }
}

function isBillingSubsidiaryEnabled(
  billingSubsidiaries: BillingSubsidiaryType[],
  campaignBillingSubsidiary: string | undefined,
  user: UserInterface
) {
  const defaultBillingSubsidiary = getDefaultBillingSubsidiaryValue(
    campaignBillingSubsidiary,
    billingSubsidiaries,
    user
  );
  const campaignAssociatedBillingSubsidiary = billingSubsidiaries.find(bs => {
    if (campaignBillingSubsidiary) {
      return bs.unit === campaignBillingSubsidiary;
    }
    if (defaultBillingSubsidiary?.length) {
      return bs.unit === defaultBillingSubsidiary[0].id;
    }
    return false;
  });
  if (campaignAssociatedBillingSubsidiary?.billable) {
    return true;
  }
  return false;
}

export function BillingSubsidiary() {
  const { formatMessage } = useIntl();
  const { getStep4, handleStep4Change, user, userRoles } = useFeesBySolutionContext();

  const { billingSubsidiaries, billingSubsidiary } = getStep4();

  const userBillingSubsidiary = getUserBillingSubsidiary(user, billingSubsidiaries);

  useEffect(() => {
    if (!billingSubsidiary && billingSubsidiaries?.length) {
      handleStep4Change({
        billingSubsidiary: getDefaultBillingSubsidiaryValue(userBillingSubsidiary, billingSubsidiaries, user)?.[0]?.id
      });
    }
  }, [billingSubsidiary, billingSubsidiaries]);

  const userHasPermission = checkUserResourcePermissions('billingSubsidiarySelector', user, userRoles);
  if (!userHasPermission) {
    return null;
  }

  const handleBillingSubsidiaryChange = (option: SelectorOptionPropsInterface) => {
    handleStep4Change({
      billingSubsidiary: (billingSubsidiaries as BillingSubsidiaryType[]).find(bs => bs.unit === option.id)?.unit
    });
  };

  if (!billingSubsidiaries?.length) {
    return (
      <BillingSubsidiaryLoadingStyled>
        <div className="billingSubsidiarySkeleton__container">
          <LoadingSkeleton height={16} width={160} />
        </div>
        <div className="billingSubsidiarySkeleton__container">
          <LoadingSkeleton height={40} />
        </div>
      </BillingSubsidiaryLoadingStyled>
    );
  }

  return (
    <Selector
      isDisabled={!isBillingSubsidiaryEnabled(billingSubsidiaries, billingSubsidiary, user)}
      label={formatMessage({
        defaultMessage: 'Billing Subsidiary',
        id: 'feesBySolution.stepFour.billingSubsidiary'
      })}
      onChange={handleBillingSubsidiaryChange}
      options={getBillingSubsidiaryOptions(billingSubsidiary, billingSubsidiaries)}
      required
      type="simple"
      value={getDefaultBillingSubsidiaryValue(billingSubsidiary ?? userBillingSubsidiary, billingSubsidiaries, user)}
    />
  );
}
